import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Typography,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Switch,
  Divider,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Edit,
  Delete,
} from "@mui/icons-material";
import CustomSnackbar from "../../newTestPrep/components/ui-component/snackbar/CustomSnackbar";

export default function TimelineTable({ refreshKey }) {
  // ====== VALUE → ID MAPS (USED FOR API PAYLOAD) ======
  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

  const quarterMap = {
    Q1: 1,
    Q2: 2,
    Q3: 3,
    Q4: 4,
  };

  const yesNoMap = {
    Yes: 1,
    No: 2,
  };

  const modeMap = {
    Online: 1,
    Offline: 2,
    Hybrid: 3,
  };

  const API_ENDPOINT2 = process.env.REACT_APP_API_ENDPOINT;

  const [openRow, setOpenRow] = useState(null);
  const [timelineData, setTimelineData] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [errors, setErrors] = useState({});
  const [streams, setStreams] = useState([]);
  const [newStream, setNewStream] = useState("");
  const [categories, setCategories] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [newActivityType, setNewActivityType] = useState("");
  const [newPlatform, setNewPlatform] = useState("");
  const [mastersLoaded, setMastersLoaded] = useState(false);

  const filteredTimelineData = timelineData.filter((row) => {
    if (!searchText) return true;

    const q = searchText.toLowerCase();

    return (
      row.task_title?.toLowerCase().includes(q) ||
      row.class_name?.toLowerCase().includes(q) ||
      row.stream_name?.toLowerCase().includes(q) ||
      row.category_name?.toLowerCase().includes(q) ||
      row.activity_type_name?.toLowerCase().includes(q)
    );
  });

  /* ================= SNACKBAR ================= */
  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  /* ================= FETCH ================= */
  const fetchTimelineData = async () => {
    try {
      const res = await fetch(`${API_ENDPOINT2}timeline/show-all-tasks`);
      const data = await res.json();

      if (res.ok) {
        setTimelineData(data.data || []);
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      console.error(err);
      // showSnackbar("Failed to fetch timeline data", "error");
    }
  };

  useEffect(() => {
    fetchTimelineData();
  }, [refreshKey]);

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this activity?"))
      return;

    try {
      const res = await fetch(
        `${API_ENDPOINT2}timeline/delete-timeline-tasks`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id }),
        },
      );

      const data = await res.json();

      if (res.ok) {
        showSnackbar("Activity deleted successfully", "success");
        fetchTimelineData();
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      console.error(err);
      // showSnackbar("Delete failed", "error");
    }
  };

  /* ================= EDIT ================= */
  const handleEditOpen = (row) => {
    setEditData({
      ...row,

      // 🔑 normalize IDs
      stream_id: row.stream ?? "",
      category_id: row.category ?? "",
      activity_type_id: row.activity_type ?? "",
      platform_id: row.platform ?? "",

      // mappings
      quarter: `Q${row.quarter}`,
      certificate: row.certificate === 1 ? "Yes" : "No",
      assessment: row.assessment === 1 ? "Yes" : "No",

      // ⚠️ IMPORTANT (see issue #3)
      mode:
        row.mode === 1
          ? "Online"
          : row.mode === 2
            ? "Offline"
            : row.mode === 3
              ? "Hybrid"
              : "",
    });

    setEditOpen(true);
  };

  const buildUpdatePayload = (row) => ({
    task_id: row.id,
    class_id: row.class_id,

    stream: row.stream_id,
    category: row.category_id,
    activityType: row.activity_type_id,
    platform: row.platform_id,

    quarter: quarterMap[row.quarter],
    certificate: yesNoMap[row.certificate],
    assessment: yesNoMap[row.assessment],
    mode: modeMap[row.mode],

    activityName: row.task_title,
    activityDescription: row.task_description,

    subActivities: row.subActivities.map((sub) => ({
      id: sub.id,
      name: sub.sub_activity_name,
      description: sub.sub_activity_description,
      duration: String(sub.duration),
      assessmentCount: String(sub.no_of_assessment),
      link: sub.link,
      is_mandatory: sub.is_mandatory,
      project_language: sub.project_language,
      tools_required: sub.tools_required,
    })),
  });

  const validateEditForm = () => {
    const newErrors = {};
    if (!editData?.class_id) newErrors.class_id = true;
    if (!editData?.stream_id) newErrors.stream_id = true;
    if (!editData?.category_id) newErrors.category_id = true;
    if (!editData?.task_title) newErrors.task_title = true;
    if (!editData?.quarter) newErrors.quarter = true;
    if (!editData?.mode) newErrors.mode = true;
    if (!editData?.task_description) newErrors.task_description = true;
    // if (!editData?.certificate) newErrors.certificate = true;
    // if (!editData?.assessment) newErrors.assessment = true;

    // Sub activities
    editData?.subActivities?.forEach((sub, index) => {
      if (!sub.sub_activity_name)
        newErrors[`sub_activity_name_${index}`] = true;
      if (!sub.sub_activity_description)
        newErrors[`sub_activity_description_${index}`] = true;
      // if (!sub.duration) newErrors[`duration_${index}`] = true;
      // if (!sub.no_of_assessment) newErrors[`no_of_assessment_${index}`] = true;
      if (!sub.link) newErrors[`link_${index}`] = true;
      // if (!sub.project_language) newErrors[`project_language_${index}`] = true;
      // if (!sub.tools_required) newErrors[`tools_required_${index}`] = true;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = async () => {
    // if (!validateEditForm()) {
    //   showSnackbar("Please fill all required fields", "error");
    //   return;
    // }
    try {
      const payload = buildUpdatePayload(editData);

      const res = await fetch(
        `${API_ENDPOINT2}timeline/update-timeline-tasks`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await res.json();

      if (res.ok) {
        showSnackbar("Activity updated successfully", "success");
        setEditOpen(false);
        fetchTimelineData();
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      console.error(err);
      // showSnackbar("Update failed", "error");
    }
  };
  const handleSubChange = (index, field, value) => {
    const updatedSubs = [...editData.subActivities];
    updatedSubs[index] = {
      ...updatedSubs[index],
      [field]: value,
    };
    setEditData({ ...editData, subActivities: updatedSubs });
  };

  const addSubActivity = () => {
    setEditData({
      ...editData,
      subActivities: [
        ...(editData.subActivities || []),
        {
          sub_activity_name: "",
          sub_activity_description: "",
          duration: "",
          no_of_assessment: "",
          link: "",
          project_language: "",
          tools_required: "",
          is_mandatory: false,
        },
      ],
    });
  };

  const removeSubActivity = (index) => {
    const updatedSubs = [...editData.subActivities];
    updatedSubs.splice(index, 1);
    setEditData({ ...editData, subActivities: updatedSubs });
  };

  const requiredSx = {
    "& .MuiFormLabel-asterisk": {
      color: "red",
    },
  };

  useEffect(() => {
    fetchMasters();
  }, []);

  const fetchMasters = async () => {
    try {
      const [streamsRes, categoriesRes, activityRes, platformRes] =
        await Promise.all([
          fetch(`${API_ENDPOINT}timeline/show-all-streams`),
          fetch(`${API_ENDPOINT}timeline/show-all-categories`),
          fetch(`${API_ENDPOINT}timeline/show-all-activity-types`),
          fetch(`${API_ENDPOINT}timeline/show-all-platforms`),
        ]);

      const streamsData = await streamsRes.json();
      const categoriesData = await categoriesRes.json();
      const activityData = await activityRes.json();
      const platformData = await platformRes.json();

      setStreams(
        streamsData.data.map((s) => ({ id: s.id, label: s.stream_name })),
      );
      setCategories(
        categoriesData.data.map((c) => ({ id: c.id, label: c.category_name })),
      );
      setActivityTypes(
        activityData.data.map((a) => ({ id: a.id, label: a.activity_type })),
      );
      setPlatforms(
        platformData.data.map((p) => ({ id: p.id, label: p.platform_name })),
      );

      setMastersLoaded(true); // ✅ IMPORTANT
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to load master data", "error");
    }
  };

  const addStream = async () => {
    if (!newStream.trim()) return;

    try {
      const res = await fetch(`${API_ENDPOINT}timeline/add-stream-name`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stream_name: newStream.trim(),
          user_id: 1,
        }),
      });

      const data = await res.json();

      if (res.status === 201) {
        setStreams((prev) => [
          ...prev,
          { id: data.data.id, label: data.data.stream_name },
        ]);

        setEditData((prev) => ({
          ...prev,
          stream_id: data.data.id,
        }));

        setNewStream("");
        showSnackbar("Stream added successfully", "success");
      }
    } catch (err) {
      showSnackbar("Failed to add stream", "error");
    }
  };

  const addCategory = async () => {
    if (!newCategory.trim()) return;

    try {
      const res = await fetch(`${API_ENDPOINT}timeline/add-category-name`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category_name: newCategory.trim(),
          user_id: 1,
        }),
      });

      const data = await res.json();

      if (res.status === 201) {
        setCategories((prev) => [
          ...prev,
          { id: data.data.id, label: data.data.category_name },
        ]);

        setEditData((prev) => ({
          ...prev,
          category_id: data.data.id,
        }));

        setNewCategory("");
        showSnackbar("Category added successfully", "success");
      }
    } catch {
      showSnackbar("Failed to add category", "error");
    }
  };
  const addActivityType = async () => {
    if (!newActivityType.trim()) return;

    try {
      const res = await fetch(`${API_ENDPOINT}timeline/add-activity-type`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          activity_type: newActivityType.trim(),
          user_id: 1,
        }),
      });

      const data = await res.json();

      if (res.status === 201) {
        setActivityTypes((prev) => [
          ...prev,
          { id: data.data.id, label: data.data.activity_type },
        ]);
        setEditData((prev) => ({
          ...prev,
          activity_type_id: data.data.id,
        }));

        // setEditData("activityType", data.data.id);
        setNewActivityType("");
        showSnackbar("Activity type added successfully", "success");
      }
    } catch {
      showSnackbar("Failed to add activity type", "error");
    }
  };
  const addPlatform = async () => {
    if (!newPlatform.trim()) return;

    try {
      const res = await fetch(`${API_ENDPOINT}timeline/add-platform-name`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          platform_name: newPlatform.trim(),
          user_id: 1,
        }),
      });

      const data = await res.json();

      if (res.status === 201) {
        setPlatforms((prev) => [
          ...prev,
          { id: data.data.id, label: data.data.platform_name },
        ]);

        setEditData((prev) => ({
          ...prev,
          platform_id: data.data.id,
        }));

        setNewPlatform("");
        showSnackbar("Platform added successfully", "success");
      }
    } catch {
      showSnackbar("Failed to add platform", "error");
    }
  };

  /* ================= UI ================= */
  return (
    <>
      <TableContainer component={Paper}>
        <Box
          p={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Typography variant="h6" color="primary" gutterBottom>
            Timeline Activity List
          </Typography>

          <TextField
            size="small"
            label="Search Activities"
            placeholder="Search by activity, class, stream, category..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ width: 280 }}
          />
        </Box>

        <CustomSnackbar
          open={snackbarOpen}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          severity={snackbarSeverity}
        />

        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F1F8F4" }}>
              <TableCell />
              <TableCell>Activity Name</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>Stream</TableCell>
              <TableCell>Quarter</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Mode</TableCell>
              <TableCell>Certificate</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredTimelineData.map((row) => (
              <React.Fragment key={row.id}>
                <TableRow hover>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setOpenRow(openRow === row.id ? null : row.id)
                      }
                    >
                      {openRow === row.id ? (
                        <KeyboardArrowUp />
                      ) : (
                        <KeyboardArrowDown />
                      )}
                    </IconButton>
                  </TableCell>

                  <TableCell>
                    <Typography fontWeight={600}>{row.task_title}</Typography>
                  </TableCell>

                  <TableCell>{row.class_name}</TableCell>
                  <TableCell>{row.stream_name}</TableCell>
                  <TableCell>{`Q${row.quarter}`}</TableCell>
                  <TableCell>{row.category_name}</TableCell>
                  <TableCell>{row.activity_type_name}</TableCell>
                  <TableCell>
                    <Chip label={row.mode_name} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.certificate_name}
                      size="small"
                      color={row.certificate === 1 ? "success" : "default"}
                    />
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton
                        color="primary"
                        onClick={() => handleEditOpen(row)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete">
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(row.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>

                {/* EXPANDED ROW */}
                <TableRow>
                  <TableCell colSpan={10} sx={{ p: 0 }}>
                    <Collapse in={openRow === row.id} unmountOnExit>
                      <Box sx={{ p: 2, backgroundColor: "#FAFAFA" }}>
                        {/* SUB ACTIVITIES */}
                        <Typography variant="subtitle2">
                          Sub Activities
                        </Typography>

                        {row.subActivities?.length ? (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Mandatory</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Duration</TableCell>
                                <TableCell>Assessments</TableCell>
                                <TableCell>Link</TableCell>
                                <TableCell>Project Language</TableCell>
                                <TableCell>Tools Required</TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {row.subActivities.map((sub, i) => (
                                <TableRow key={sub.id || i}>
                                  <TableCell>{i + 1}</TableCell>
                                  <TableCell>
                                    {sub.is_mandatory ? "Yes" : "No"}
                                  </TableCell>
                                  <TableCell>{sub.sub_activity_name}</TableCell>
                                  <TableCell>
                                    {sub.sub_activity_description}
                                  </TableCell>
                                  <TableCell>{sub.duration} hrs</TableCell>
                                  <TableCell>{sub.no_of_assessment}</TableCell>
                                  <TableCell>
                                    <a
                                      href={
                                        sub.link?.startsWith("http")
                                          ? sub.link
                                          : `https://${sub.link}`
                                      }
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Open
                                    </a>
                                  </TableCell>
                                  <TableCell>{sub.project_language}</TableCell>
                                  <TableCell>{sub.tools_required}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <Typography color="text.secondary">
                            No sub activities added.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ================= EDIT MODAL ================= */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Timeline Activity</DialogTitle>

        <DialogContent dividers>
          <Box display="flex" gap={2} my={2}>
            <TextField
              select
              label="Class"
              value={editData?.class_id || ""}
              onChange={(e) =>
                setEditData({ ...editData, class_id: Number(e.target.value) })
              }
              sx={{ mb: 2 }}
            >
              <MenuItem value={1}>Class 9</MenuItem>
              <MenuItem value={2}>Class 10</MenuItem>
              <MenuItem value={3}>Class 11</MenuItem>
              <MenuItem value={4}>Class 12</MenuItem>
            </TextField>
            <TextField
              select
              required
              label="Stream"
              value={editData?.stream_id ?? ""}
              onChange={(e) =>
                setEditData((prev) => ({
                  ...prev,
                  stream_id: Number(e.target.value),
                }))
              }
              error={!editData?.stream_id}
              sx={requiredSx}
              SelectProps={{
                MenuProps: {
                  disableAutoFocusItem: true,
                  onKeyDown: (e) => e.stopPropagation(),
                },
              }}
            >
              {streams.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.label}
                </MenuItem>
              ))}

              <Divider />

              <MenuItem
                disableRipple
                disableTouchRipple
                onClick={(e) => e.stopPropagation()}
              >
                <Box display="flex" gap={1} width="100%">
                  <TextField
                    size="small"
                    placeholder="Enter stream"
                    value={newStream}
                    onChange={(e) => setNewStream(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();
                      addStream();
                    }}
                  >
                    Save
                  </Button>
                </Box>
              </MenuItem>
            </TextField>

            <TextField
              select
              required
              label="Category"
              value={editData?.category_id ?? ""}
              onChange={(e) =>
                setEditData((prev) => ({
                  ...prev,
                  category_id: Number(e.target.value),
                }))
              }
              error={!editData?.category_id}
              sx={requiredSx}
              SelectProps={{
                MenuProps: {
                  disableAutoFocusItem: true,
                  onKeyDown: (e) => e.stopPropagation(),
                },
              }}
            >
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.label}
                </MenuItem>
              ))}

              <Divider />

              <MenuItem
                disableRipple
                disableTouchRipple
                onClick={(e) => e.stopPropagation()}
              >
                <Box display="flex" gap={1} width="100%">
                  <TextField
                    size="small"
                    placeholder="Enter category"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();
                      addCategory();
                    }}
                  >
                    Save
                  </Button>
                </Box>
              </MenuItem>
            </TextField>

            <TextField
              select
              label="Platform"
              value={editData?.platform_id ?? ""}
              onChange={(e) =>
                setEditData((prev) => ({
                  ...prev,
                  platform_id: Number(e.target.value),
                }))
              }
              SelectProps={{
                MenuProps: {
                  disableAutoFocusItem: true,
                  onKeyDown: (e) => e.stopPropagation(),
                },
              }}
            >
              {platforms.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.label}
                </MenuItem>
              ))}

              <Divider />

              <MenuItem
                disableRipple
                disableTouchRipple
                onClick={(e) => e.stopPropagation()}
              >
                <Box display="flex" gap={1} width="100%">
                  <TextField
                    size="small"
                    placeholder="Enter platform"
                    value={newPlatform}
                    onChange={(e) => setNewPlatform(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();
                      addPlatform();
                    }}
                  >
                    Save
                  </Button>
                </Box>
              </MenuItem>
            </TextField>
          </Box>
          {/* ACTIVITY NAME */}
          <Box display="flex" gap={2} my={2}>
            <TextField
              required
              sx={requiredSx}
              error={errors.task_title}
              label="Activity Name"
              value={editData?.task_title || ""}
              onChange={(e) =>
                setEditData({ ...editData, task_title: e.target.value })
              }
            />
            <TextField
              required
              sx={requiredSx}
              error={errors.quater}
              select
              label="Quarter"
              value={editData?.quarter || ""}
              onChange={(e) =>
                setEditData({ ...editData, quarter: e.target.value })
              }
            >
              {["Q1", "Q2", "Q3", "Q4"].map((q) => (
                <MenuItem key={q} value={q}>
                  {q}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              required
              sx={requiredSx}
              select
              error={errors.mode}
              label="Mode"
              value={editData?.mode || ""}
              onChange={(e) =>
                setEditData({ ...editData, mode: e.target.value })
              }
            >
              {["Online", "Offline", "Hybrid"].map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {/* ACTIVITY DESCRIPTION */}
          <TextField
            required
            sx={requiredSx}
            error={errors.task_description}
            fullWidth
            margin="dense"
            label="Description"
            multiline
            rows={3}
            value={editData?.task_description || ""}
            onChange={(e) =>
              setEditData({ ...editData, task_description: e.target.value })
            }
          />

          <Box display="flex" gap={2} mt={2}>
            <TextField
              select
              label="Activity Type"
              value={editData?.activity_type_id ?? ""}
              onChange={(e) =>
                setEditData((prev) => ({
                  ...prev,
                  activity_type_id: Number(e.target.value),
                }))
              }
              SelectProps={{
                MenuProps: {
                  disableAutoFocusItem: true,
                  onKeyDown: (e) => e.stopPropagation(),
                },
              }}
            >
              {activityTypes.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.label}
                </MenuItem>
              ))}

              <Divider />

              <MenuItem
                disableRipple
                disableTouchRipple
                onClick={(e) => e.stopPropagation()}
              >
                <Box display="flex" gap={1} width="100%">
                  <TextField
                    size="small"
                    placeholder="Enter activity type"
                    value={newActivityType}
                    onChange={(e) => setNewActivityType(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();
                      addActivityType();
                    }}
                  >
                    Save
                  </Button>
                </Box>
              </MenuItem>
            </TextField>

            <TextField
              select
              label="Certificate"
              value={editData?.certificate || ""}
              onChange={(e) =>
                setEditData({ ...editData, certificate: e.target.value })
              }
            >
              {["Yes", "No"].map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Assessment"
              fullWidth
              value={editData?.assessment || ""}
              onChange={(e) =>
                setEditData({
                  ...editData,
                  assessment: e.target.value,
                })
              }
            >
              {["Yes", "No"].map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* SUB ACTIVITIES */}
          <Box mt={4}>
            <Typography fontWeight={600}>Sub Activities</Typography>

            {editData?.subActivities?.map((sub, index) => (
              <Box
                key={index}
                mt={2}
                p={2}
                border="1px solid #ddd"
                borderRadius={1}
              >
                <Box display="flex" gap={2} mt={2}>
                  <TextField
                    required
                    sx={requiredSx}
                    error={errors[`sub_activity_name_${index}`]}
                    fullWidth
                    margin="dense"
                    label="Sub Activity Name"
                    value={sub.sub_activity_name || ""}
                    onChange={(e) =>
                      handleSubChange(
                        index,
                        "sub_activity_name",
                        e.target.value,
                      )
                    }
                  />

                  <TextField
                    fullWidth
                    margin="dense"
                    required
                    sx={requiredSx}
                    error={errors[`sub_activity_description_${index}`]}
                    label="Description"
                    value={sub.sub_activity_description || ""}
                    onChange={(e) =>
                      handleSubChange(
                        index,
                        "sub_activity_description",
                        e.target.value,
                      )
                    }
                  />
                </Box>
                <Box display="flex" gap={2} mt={1}>
                  <TextField
                    label="Duration (hrs)"
                    type="number"
                    fullWidth
                    value={sub.duration || ""}
                    onChange={(e) =>
                      handleSubChange(index, "duration", Number(e.target.value))
                    }
                  />

                  <TextField
                    label="Assessments"
                    type="number"
                    fullWidth
                    value={sub.no_of_assessment || ""}
                    onChange={(e) =>
                      handleSubChange(
                        index,
                        "no_of_assessment",
                        Number(e.target.value),
                      )
                    }
                  />
                </Box>
                <Box display="flex" gap={2} mt={1}>
                  <TextField
                    margin="dense"
                    label="Project Language"
                    fullWidth
                    value={sub.project_language || ""}
                    onChange={(e) =>
                      handleSubChange(index, "project_language", e.target.value)
                    }
                  />
                  <TextField
                    margin="dense"
                    label="Tools Required"
                    fullWidth
                    value={sub.tools_required || ""}
                    onChange={(e) =>
                      handleSubChange(index, "tools_required", e.target.value)
                    }
                  />
                </Box>

                <TextField
                  required
                  sx={requiredSx}
                  error={errors[`link_${index}`]}
                  fullWidth
                  margin="dense"
                  label="Resource Link"
                  value={sub.link || ""}
                  onChange={(e) =>
                    handleSubChange(index, "link", e.target.value)
                  }
                />

                <Box
                  display="flex"
                  gap={2}
                  justifyContent="space-between"
                  mt={1}
                >
                  <Switch
                    label="Mandatory"
                    checked={sub.is_mandatory}
                    onChange={(e) =>
                      handleSubChange(index, "is_mandatory", e.target.checked)
                    }
                    color="primary"
                  />
                  <Button
                    color="error"
                    size="small"
                    onClick={() => removeSubActivity(index)}
                  >
                    Remove
                  </Button>
                </Box>
              </Box>
            ))}

            <Button sx={{ mt: 2 }} variant="outlined" onClick={addSubActivity}>
              + Add Sub Activity
            </Button>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
